import { all, put, take, race, select, takeEvery } from 'redux-saga/effects'
import { SUCCESS, FAILED } from 'crom-api-constants'
import _ from 'lodash'

import { getLocale, getToken } from '@global/selectors'

import config from '../../../../../../../../../../../config'

import {
    ADDRESSES_RUIAN_ID_CHANGE,
    ADDRESSES_GET_FIRMS_ON_ADDRESS_SUMMARY_REQUEST,
    ADDRESSES_GET_FIRMS_ON_ADDRESS_TABLE_REQUEST,
    ADDRESSES_FIRMS_ON_ADDRESS_TABLE_CHANGE,
} from './constants'
import {
    addressesGetFirmsOnAddressSummaryRequest,
    addressesGetFirmsOnAddressTableRequest,
    addressesFirmsOnAddressDataReceived,
    addressesFirmsOnAddressTableLoadingTrue,
    addressesFirmsOnAddressTableLoadingFalse,
} from './actions'

const ADDRESSES_GET_FIRMS_ON_ADDRESS_SUMMARY_REQUEST_SUCCESS = (id) =>
    `${ADDRESSES_GET_FIRMS_ON_ADDRESS_SUMMARY_REQUEST(id)}_${SUCCESS}`
const ADDRESSES_GET_FIRMS_ON_ADDRESS_SUMMARY_REQUEST_FAILED = (id) =>
    `${ADDRESSES_GET_FIRMS_ON_ADDRESS_SUMMARY_REQUEST(id)}_${FAILED}`

const ADDRESSES_GET_FIRMS_ON_ADDRESS_TABLE_REQUEST_SUCCESS = (id) =>
    `${ADDRESSES_GET_FIRMS_ON_ADDRESS_TABLE_REQUEST(id)}_${SUCCESS}`
const ADDRESSES_GET_FIRMS_ON_ADDRESS_TABLE_REQUEST_FAILED = (id) =>
    `${ADDRESSES_GET_FIRMS_ON_ADDRESS_TABLE_REQUEST(id)}_${FAILED}`

function* ruianIdChangeCallSaga(action) {
    const blockKey = _.get(action, 'data.block', null)
    if (blockKey !== config.modules.report.blocks.addresses.key) return null

    const module = _.get(action, 'data.module', null)
    const id = _.get(action, 'data.id', null)
    const ruianId = _.get(action, 'data.ruianId', null)
    if (!module || !id || !blockKey || !ruianId) return null

    const customData = _.get(action, 'data.customData', null)
    const level = _.get(action, 'data.level', null)
    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)
    const lang = config.languages[locale].requestKey

    const summaryResponse = yield all({
        response: race({
            success: take(ADDRESSES_GET_FIRMS_ON_ADDRESS_SUMMARY_REQUEST_SUCCESS(id)),
            failed: take(ADDRESSES_GET_FIRMS_ON_ADDRESS_SUMMARY_REQUEST_FAILED(id)),
        }),
        request: put(addressesGetFirmsOnAddressSummaryRequest(module, id, blockKey, level, token, lang, ruianId)),
    })

    const tableResponse = yield all({
        response: race({
            success: take(ADDRESSES_GET_FIRMS_ON_ADDRESS_TABLE_REQUEST_SUCCESS(id)),
            failed: take(ADDRESSES_GET_FIRMS_ON_ADDRESS_TABLE_REQUEST_FAILED(id)),
        }),
        request: put(
            addressesGetFirmsOnAddressTableRequest(module, id, blockKey, level, token, lang, ruianId, {
                page: 0,
                rows: 10,
            })
        ),
    })

    if (summaryResponse.response.success && tableResponse.response.success) {
        const summaryData = _.get(summaryResponse, 'response.success.payload.response.body', {})
        const tableData = _.get(tableResponse, 'response.success.payload.response.body', {})

        yield put(
            addressesFirmsOnAddressDataReceived(module, id, blockKey, {
                ...customData,
                firmsOnAddress: {
                    summary: summaryData,
                    table: {
                        data: _.get(tableData, 'rows', []),
                        pagination: _.get(tableData, 'query.pagination', {}),
                        sorted: [],
                    },
                },
            })
        )
    }

    return null
}

function* tableChangeSaga(action) {
    const module = _.get(action, 'data.module', null)
    const blockKey = _.get(action, 'data.blockKey', null)
    const id = _.get(action, 'data.id', null)
    const level = _.get(action, 'data.level', null)
    const ruianId = _.get(action, 'data.ruianId', null)
    const queryParams = _.get(action, 'data.queryParams', null)
    const customData = _.get(action, 'data.customData', {})

    const locale = yield select(getLocale)
    const token = yield select(getToken, id, module)

    const lang = config.languages[locale].requestKey

    if (!module || !blockKey || !id || !level || !ruianId) return null

    yield put(
        addressesFirmsOnAddressTableLoadingTrue(module, id, blockKey, {
            ...customData,
            firmsOnAddress: {
                ...customData.firmsOnAddress,
                table: {
                    ...customData.firmsOnAddress.table,
                    isLoading: true,
                },
            },
        })
    )

    const qp = {
        // filters: [],
        'sort[]': _.map(_.get(queryParams, 'sorted', []), (val) => `${_.get(val, 'id')}${val.desc ? ':desc' : ':asc'}`),
        page: _.get(queryParams, 'pagination.page'),
        rows: _.get(queryParams, 'pagination.rowsPerPage'),
    }

    const response = yield all({
        response: race({
            success: take(ADDRESSES_GET_FIRMS_ON_ADDRESS_TABLE_REQUEST_SUCCESS(id)),
            failed: take(ADDRESSES_GET_FIRMS_ON_ADDRESS_TABLE_REQUEST_FAILED(id)),
        }),
        request: put(addressesGetFirmsOnAddressTableRequest(module, id, blockKey, level, token, lang, ruianId, qp)),
    })

    if (!response.response.failed) {
        const data = _.map(_.get(response, 'response.success.payload.response.body.rows'), (row) => ({ ...row }))
        const query = _.get(response, 'response.success.payload.response.body.query', [])
        const pagination = _.get(query, 'pagination', null)
        const sorted = _.reduce(
            _.get(query, 'sort.sortedColumns'),
            (acc, val) => [
                ...acc,
                {
                    id: _.get(val, 'columm'), // TYPO FROM BACKEND
                    desc: _.get(val, 'direction') === 'desc',
                },
            ],
            []
        )

        const newCustomData = {
            ...customData,
            firmsOnAddress: {
                ...customData.firmsOnAddress,
                table: {
                    data,
                    pagination,
                    sorted,
                    isLoading: false,
                },
            },
        }

        yield put(addressesFirmsOnAddressDataReceived(module, id, blockKey, newCustomData))
    } else {
        yield put(
            addressesFirmsOnAddressTableLoadingFalse(module, id, blockKey, {
                ...customData,
                firmsOnAddress: {
                    ...customData.firmsOnAddress,
                    table: {
                        ...customData.firmsOnAddress.table,
                        isLoading: false,
                    },
                },
            })
        )
    }

    return null
}

export default function* () {
    yield all([
        takeEvery(ADDRESSES_RUIAN_ID_CHANGE, ruianIdChangeCallSaga),
        takeEvery(ADDRESSES_FIRMS_ON_ADDRESS_TABLE_CHANGE, tableChangeSaga),
    ])
}
